import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { tournamentLobbyService } from '../../services/TournamentLobbyService';
import type { TournamentLobby } from '../../types/tournament';
import { useToast } from './ToastContainer';

interface InvitationsContextType {
  pendingInvitations: TournamentLobby[];
  loading: boolean;
}

const InvitationsContext = createContext<InvitationsContextType | undefined>(undefined);

export const useInvitations = () => {
  const context = useContext(InvitationsContext);
  if (!context) {
    throw new Error('useInvitations must be used within InvitationsProvider');
  }
  return context;
};

export const InvitationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [pendingInvitations, setPendingInvitations] = useState<TournamentLobby[]>([]);
  const [loading, setLoading] = useState(true);
  const seenInvitationIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      setPendingInvitations([]);
      setLoading(false);
      seenInvitationIds.current = new Set();
      return;
    }

    setLoading(true);
    let isFirstLoad = true;

    const unsubscribe = tournamentLobbyService.subscribeToMyInvitations(user.uid, (invitations) => {
      if (isFirstLoad) {
        seenInvitationIds.current = new Set(invitations.map((i) => i.id));
        isFirstLoad = false;
        setPendingInvitations(invitations);
        setLoading(false);
        return;
      }

      invitations.forEach((invitation) => {
        if (!seenInvitationIds.current.has(invitation.id)) {
          const host = invitation.participants.find((p) => p.userId === invitation.hostUserId);
          showToast({
            type: 'info',
            title: '🎮 Tournament Invitation',
            message: `${host?.displayName || 'Someone'} invited you to a tournament!`,
            action: {
              label: 'View',
              onClick: () => navigate(`/tournament/lobby/${invitation.id}`),
            },
            duration: 10000,
          });
          seenInvitationIds.current.add(invitation.id);
        }
      });

      seenInvitationIds.current = new Set(invitations.map((i) => i.id));
      setPendingInvitations(invitations);
    });

    return () => {
      unsubscribe();
    };
  }, [user, navigate, showToast]);

  return (
    <InvitationsContext.Provider value={{ pendingInvitations, loading }}>
      {children}
    </InvitationsContext.Provider>
  );
};
